import axios from 'axios';

import tokenServices from '../services/tokenServices';

// Use env variable if set, otherwise use window.location.origin
export const API_URL = process.env.REACT_APP_API_URL || `${window.location.origin}/api/v1`;

axios.defaults.baseURL = API_URL.replace('/api/v1', '');

const instance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Refresh token lock to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

instance.interceptors.request.use(
  (config) => {
    const token = tokenServices.getLocalToken();

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Version check: compare X-App-Version header from nginx with the loaded app version
let _loadedVersion = null;
let _versionMismatchReloading = false;

function checkVersionHeader(response) {
  const serverVersion = response.headers?.['x-app-version'];
  if (!serverVersion) return;
  if (!_loadedVersion) {
    _loadedVersion = serverVersion;
    return;
  }
  if (serverVersion !== _loadedVersion && !_versionMismatchReloading) {
    _versionMismatchReloading = true;
    tokenServices.removeTokens();
    window.location.reload();
  }
}

instance.interceptors.response.use(
  (res) => {
    checkVersionHeader(res);
    return res;
  },
  async (err) => {
    const originalConfig = err.config;
    if (originalConfig.url !== '/auth/login' && err.response) {
      // Access Token was expired
      if (err.response.status === 401 && !originalConfig._retry) {
        // If already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalConfig.headers['Authorization'] = `Bearer ${token}`;
              return instance(originalConfig);
            })
            .catch((error) => {
              return Promise.reject(error);
            });
        }

        originalConfig._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = tokenServices.getLocalRefreshToken();

          if (!refreshToken) {
            throw new Error('No refresh token available');
          }

          const response = await axios.get(`${API_URL}/auth/refreshToken`, {
            headers: { Authorization: `Bearer ${refreshToken}` },
          });

          if (response.status === 200 && response.data.token) {
            tokenServices.updateLocalTokens(response.data.token, response.data.refreshToken);
            processQueue(null, response.data.token);
            originalConfig.headers['Authorization'] = `Bearer ${response.data.token}`;
            return instance(originalConfig);
          }

          throw new Error('Invalid refresh response');
        } catch (_error) {
          processQueue(_error, null);
          tokenServices.removeTokens();
          window.location.replace('/');
          return Promise.reject(_error);
        } finally {
          isRefreshing = false;
        }
      }
    }
    return Promise.reject(err);
  },
);

export default instance;
